import { useState, useEffect } from "react";
import { financeApi, platformApi, remindersApi } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CurrencyDollar, HandCoins, Receipt, Bank, Warning, TrendDown, TrendUp, PaperPlaneTilt, X } from "@phosphor-icons/react";
import { format } from "date-fns";
import { toast } from "sonner";

/**
 * Drill-down for the 5 admin/finance KPIs:
 *   topic = income | payouts | expenses | net | bank | outstanding
 *
 * Reuses existing finance endpoints. Outstanding pulls from the new
 * /finance/outstanding-breakdown endpoint.
 */
export default function AdminKpiDrillDown({ topic, onClose }) {
  const open = Boolean(topic);
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid={`admin-kpi-drilldown-${topic}`}>
        {topic === "income"      && <IncomeView />}
        {topic === "payouts"     && <PayoutsView />}
        {topic === "expenses"    && <ExpensesView />}
        {topic === "net"         && <NetSurplusView />}
        {topic === "bank"        && <BankView />}
        {topic === "outstanding" && <OutstandingView />}
      </DialogContent>
    </Dialog>
  );
}

function Section({ icon: Icon, color, title, description, children }) {
  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2"><Icon className={`w-5 h-5 ${color}`} weight="duotone" /> {title}</DialogTitle>
        {description && <DialogDescription>{description}</DialogDescription>}
      </DialogHeader>
      <div className="mt-3 space-y-3">{children}</div>
    </>
  );
}

function StatPills({ items }) {
  return (
    <div className={`grid grid-cols-${Math.min(items.length, 4)} gap-2`}>
      {items.map((it) => (
        <div key={it.label} className={`rounded-xl px-3 py-3 text-center ${it.cls || "bg-secondary/40"}`}>
          <p className="text-xl font-bold font-body">{it.value}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{it.label}</p>
        </div>
      ))}
    </div>
  );
}

function IncomeView() {
  const [overview, setOverview] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  useEffect(() => {
    financeApi.overview().then(({ data }) => setOverview(data)).catch(() => setOverview({}));
    platformApi.incomeBreakdown?.().then?.(({ data }) => setBreakdown(data)).catch(() => {});
  }, []);
  if (!overview) return <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>;
  const t = overview.totals || {};
  return (
    <Section icon={CurrencyDollar} color="text-emerald-700" title="Total income" description="Every shilling that has come into the welfare fund — bereavement contributions plus member registration fees.">
      <StatPills items={[
        { label: "Total income",        value: `KES ${Math.round(t.contributionsCollected || 0).toLocaleString()}`, cls: "bg-emerald-50 border border-emerald-200" },
        { label: "Bereavement contrib.", value: `KES ${Math.round(t.bereavementContributions || 0).toLocaleString()}` },
        { label: "Registration fees",    value: `KES ${Math.round(t.registrationFees || 0).toLocaleString()}` },
      ]} />
      {breakdown && (
        <div className="text-xs text-muted-foreground border-t border-border pt-3 leading-relaxed">
          <p className="font-medium text-foreground mb-1">Reconciliation against the spreadsheet</p>
          <p>{(breakdown.bereavementCount ?? "—")} bereavements logged. Income reconciles against the May 2026 register footer (KES 1,278,200 contributions + KES 105,300 reg fees = KES 1,383,500).</p>
        </div>
      )}
    </Section>
  );
}

function PayoutsView() {
  const [rows, setRows] = useState(null);
  useEffect(() => { financeApi.listPayouts?.().then(({ data }) => setRows(data)).catch(() => setRows([])); }, []);
  const total = (rows || []).reduce((s, r) => s + (r.amount || 0), 0);
  return (
    <Section icon={HandCoins} color="text-orange-700" title="Benefit payouts" description="Disbursements made to bereaved families. Each row is one payout transaction.">
      <StatPills items={[
        { label: "Total disbursed", value: `KES ${total.toLocaleString()}`, cls: "bg-orange-50 border border-orange-200" },
        { label: "Payouts logged",  value: (rows || []).length },
      ]} />
      <div className="border border-border rounded-xl divide-y divide-border max-h-[55vh] overflow-y-auto">
        {(rows || []).length === 0 && <p className="text-center py-6 text-sm text-muted-foreground">No payouts yet.</p>}
        {(rows || []).map((r) => (
          <div key={r.id} className="px-4 py-3 flex items-center justify-between gap-3 text-sm">
            <div className="min-w-0">
              <p className="font-medium truncate">{r.paidTo || "Family"}</p>
              <p className="text-xs text-muted-foreground truncate">
                {r.deceasedName || "—"}{r.paidVia ? ` · ${r.paidVia}` : ""}{r.date ? ` · ${format(new Date(r.date), "dd MMM yyyy")}` : ""}
              </p>
            </div>
            <span className="font-mono text-orange-700 font-semibold whitespace-nowrap">KES {Math.round(r.amount || 0).toLocaleString()}</span>
          </div>
        ))}
      </div>
    </Section>
  );
}

function ExpensesView() {
  const [rows, setRows] = useState(null);
  useEffect(() => { financeApi.listExpenses?.().then(({ data }) => setRows(data)).catch(() => setRows([])); }, []);
  const total = (rows || []).reduce((s, r) => s + (r.amount || 0), 0);
  return (
    <Section icon={Receipt} color="text-red-700" title="Expenses" description="Operating costs — rent, stationery, transport, etc.">
      <StatPills items={[
        { label: "Total expenses", value: `KES ${total.toLocaleString()}`, cls: "bg-red-50 border border-red-200" },
        { label: "Entries",        value: (rows || []).length },
      ]} />
      <div className="border border-border rounded-xl divide-y divide-border max-h-[55vh] overflow-y-auto">
        {(rows || []).length === 0 && <p className="text-center py-6 text-sm text-muted-foreground">No expenses logged.</p>}
        {(rows || []).map((r) => (
          <div key={r.id} className="px-4 py-3 flex items-center justify-between gap-3 text-sm">
            <div className="min-w-0">
              <p className="font-medium truncate">{r.description || r.categoryName || "Expense"}</p>
              <p className="text-xs text-muted-foreground truncate">
                {r.categoryName || "—"}{r.date ? ` · ${format(new Date(r.date), "dd MMM yyyy")}` : ""}
              </p>
            </div>
            <span className="font-mono text-red-700 font-semibold whitespace-nowrap">KES {Math.round(r.amount || 0).toLocaleString()}</span>
          </div>
        ))}
      </div>
    </Section>
  );
}

function NetSurplusView() {
  const [overview, setOverview] = useState(null);
  useEffect(() => { financeApi.overview().then(({ data }) => setOverview(data)).catch(() => setOverview({})); }, []);
  if (!overview) return <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>;
  const t = overview.totals || {};
  const net = t.netSurplus || 0;
  const Icon = net >= 0 ? TrendUp : TrendDown;
  return (
    <Section icon={Icon} color={net >= 0 ? "text-primary" : "text-red-700"} title={net >= 0 ? "Net surplus" : "Net deficit"} description="Total income minus benefit payouts and operating expenses. This is the welfare fund's true bottom line.">
      <StatPills items={[
        { label: net >= 0 ? "Net surplus" : "Net deficit", value: `KES ${Math.round(net).toLocaleString()}`, cls: net >= 0 ? "bg-primary/10 border border-primary/30" : "bg-red-50 border border-red-200" },
        { label: "Income",   value: `KES ${Math.round(t.contributionsCollected || 0).toLocaleString()}` },
        { label: "Payouts",  value: `KES ${Math.round(t.benefitPayouts || 0).toLocaleString()}` },
        { label: "Expenses", value: `KES ${Math.round(t.expenses || 0).toLocaleString()}` },
      ]} />
      <div className="text-xs text-muted-foreground border-t border-border pt-3 leading-relaxed">
        Net surplus = Total income − Benefit payouts − Expenses.
      </div>
    </Section>
  );
}

function BankView() {
  const [overview, setOverview] = useState(null);
  useEffect(() => { financeApi.overview().then(({ data }) => setOverview(data)).catch(() => setOverview({})); }, []);
  if (!overview) return <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>;
  const ba = overview.bankAccount || {};
  return (
    <Section icon={Bank} color="text-foreground" title={`Bank balance · ${ba.name || "—"}`} description="Live bank balance based on opening + deposits − withdrawals you've recorded in the Banking tab.">
      <StatPills items={[
        { label: "Balance",     value: `KES ${Math.round(ba.balance || 0).toLocaleString()}`, cls: "bg-secondary/40" },
        { label: "Deposits",    value: `KES ${Math.round(ba.deposits || 0).toLocaleString()}` },
        { label: "Withdrawals", value: `KES ${Math.round(ba.withdrawals || 0).toLocaleString()}` },
        { label: "Opening",     value: `KES ${Math.round(ba.openingBalance || 0).toLocaleString()}` },
      ]} />
      <div className="text-xs text-muted-foreground border-t border-border pt-3">
        {ba.accountNumber ? <>Account: <code className="font-mono">{ba.accountNumber}</code></> : "Set the bank account in System → Banking."}
      </div>
    </Section>
  );
}

function OutstandingView() {
  const [data, setData] = useState(null);
  const [reminderRow, setReminderRow] = useState(null);
  useEffect(() => { financeApi.outstandingBreakdown().then(({ data }) => setData(data)).catch(() => setData({ rows: [] })); }, []);
  if (!data) return <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>;
  return (
    <Section icon={Warning} color="text-amber-700" title="Outstanding contributions" description="Per-event shortfall: how much each bereavement is short of the full collection (active members × per-event amount). The total is the welfare fund's deficit carried on its books.">
      <StatPills items={[
        { label: "Total shortfall",    value: `KES ${Math.round(data.totalShortfall || 0).toLocaleString()}`, cls: "bg-amber-50 border border-amber-200" },
        { label: "Events affected",    value: data.bereavementsWithShortfall || 0 },
        { label: "Active members",     value: data.activeMembers || 0 },
      ]} />
      <div className="border border-border rounded-xl overflow-hidden">
        <table className="w-full text-xs sm:text-sm">
          <thead className="bg-secondary/40 text-left">
            <tr>
              <th className="px-3 py-2">Event</th>
              <th className="px-3 py-2 text-right">Expected</th>
              <th className="px-3 py-2 text-right">Collected</th>
              <th className="px-3 py-2 text-right">Shortfall</th>
              <th className="px-3 py-2 text-right">Members</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {(data.rows || []).map((r) => (
              <tr key={r.id} className={r.shortfall > 0 ? "" : "opacity-60"}>
                <td className="px-3 py-2">
                  <p className="font-medium truncate max-w-[18rem]">{r.deceasedName}</p>
                  <p className="text-[10px] text-muted-foreground">
                    KES {Math.round(r.contributionAmount).toLocaleString()}/member
                    {r.dateOfDeath ? ` · ${format(new Date(r.dateOfDeath), "dd MMM yyyy")}` : ""}
                  </p>
                </td>
                <td className="px-3 py-2 font-mono text-right">{Math.round(r.expected).toLocaleString()}</td>
                <td className="px-3 py-2 font-mono text-right">{Math.round(r.collected).toLocaleString()}</td>
                <td className={`px-3 py-2 font-mono text-right font-semibold ${r.shortfall > 0 ? "text-amber-700" : "text-muted-foreground"}`}>
                  {Math.round(r.shortfall).toLocaleString()}
                </td>
                <td className="px-3 py-2 font-mono text-right">{r.membersOutstanding}</td>
                <td className="px-3 py-2 text-right">
                  {r.shortfall > 0 ? (
                    <button
                      onClick={() => setReminderRow(r)}
                      data-testid={`remind-btn-${r.id}`}
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-lg bg-primary text-primary-foreground font-semibold active:scale-95"
                    >
                      <PaperPlaneTilt className="w-3 h-3" weight="fill" /> Remind
                    </button>
                  ) : <span className="text-muted-foreground text-xs">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-muted-foreground leading-relaxed">
        Shortfall = (active members × KES per event) − total collected for that event. The "Members" column is the number of members who have not yet contributed for that bereavement.
      </p>
      {reminderRow && <ReminderDialog row={reminderRow} onClose={() => setReminderRow(null)} />}
    </Section>
  );
}

// ─── Reminder dialog ─────────────────────────────────────────────────────────
function ReminderDialog({ row, onClose }) {
  const [channel, setChannel] = useState("all");
  const [customMessage, setCustomMessage] = useState("");
  const [includeContributed, setIncludeContributed] = useState(false);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);

  // Dry-run on open to count recipients
  useEffect(() => {
    remindersApi
      .forBereavement(row.id, { channel: "all", dryRun: true, includeContributed })
      .then(({ data }) => setPreview(data))
      .catch(() => setPreview(null));
  }, [row.id, includeContributed]);

  const send = async () => {
    setSending(true);
    try {
      const { data } = await remindersApi.forBereavement(row.id, {
        channel,
        customMessage: customMessage.trim() || undefined,
        includeContributed,
      });
      const parts = [];
      if (data.sms?.sent) parts.push(`SMS ${data.sms.sent}`);
      if (data.sms?.failed) parts.push(`SMS-fail ${data.sms.failed}`);
      if (data.push?.delivered) parts.push(`push ${data.push.delivered}`);
      toast.success(`Reminder sent to ${data.recipients} members${parts.length ? ` (${parts.join(", ")})` : ""}`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send reminders");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="reminder-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PaperPlaneTilt className="w-5 h-5 text-primary" weight="fill" /> Send reminder
          </DialogTitle>
          <DialogDescription>
            Notify members who haven't contributed for <strong>{row.deceasedName}</strong>.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="grid grid-cols-3 gap-2">
            {[
              { v: "all", l: "SMS + Push" },
              { v: "sms", l: "SMS only" },
              { v: "push", l: "Push only" },
            ].map((o) => (
              <button
                key={o.v}
                onClick={() => setChannel(o.v)}
                data-testid={`reminder-channel-${o.v}`}
                className={`px-2 py-2 rounded-lg text-xs font-semibold border ${channel === o.v ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border"}`}
              >
                {o.l}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={includeContributed} onChange={(e) => setIncludeContributed(e.target.checked)} />
            Also notify members who already contributed (e.g., thank-you note)
          </label>
          <textarea
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            placeholder="Custom message (leave blank for the default reminder)"
            rows={3}
            data-testid="reminder-custom-message"
            className="w-full px-3 py-2 text-xs bg-card border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring resize-none"
          />
          {preview && (
            <div className="bg-secondary/40 rounded-xl p-3 text-xs text-muted-foreground">
              <p><strong className="text-foreground">Recipients: {preview.wouldNotify}</strong> (of {preview.totalActive} active · {preview.alreadyPaid} already paid)</p>
              <p className="mt-1 italic line-clamp-3">"{customMessage.trim() || preview.preview}"</p>
            </div>
          )}
          <div className="flex gap-2">
            <button
              onClick={send}
              disabled={sending || !preview || preview?.wouldNotify === 0}
              data-testid="reminder-send-btn"
              className="flex-1 py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm active:scale-95 disabled:opacity-60"
            >
              {sending ? "Sending…" : `Send to ${preview?.wouldNotify ?? "…"} members`}
            </button>
            <button
              onClick={onClose}
              disabled={sending}
              className="px-4 py-2.5 rounded-xl bg-secondary text-secondary-foreground border border-border font-medium text-sm"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
